'use client';

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { authModalStore, openAuth, closeAuth } from '@/lib/authModalStore';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';

/**
 * AuthModal — the single global sign-in / sign-up surface.
 *
 * Mounted once in Router. Opened from anywhere via openAuth('login'|'register')
 * so "Log in" / "Get started" / the build CTA never route to a separate page —
 * they pop this modal in place. Hosts the exact same LoginForm/RegisterForm the
 * standalone /login and /register pages use (Privy primary + email/password),
 * so behavior stays identical. On successful auth the forms navigate to /chat;
 * the route change auto-closes the modal.
 */
export default function AuthModal() {
  const snap = useSnapshot(authModalStore);
  const location = useLocation();

  // Any navigation (e.g. auth success → /chat) closes the modal.
  useEffect(() => {
    closeAuth();
  }, [location.pathname]);

  // ESC to close.
  useEffect(() => {
    if (!snap.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuth();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [snap.open]);

  if (!snap.open) return null;

  return (
    <div
      onClick={closeAuth}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-xl"
      >
        <button
          onClick={closeAuth}
          aria-label="Close"
          className="absolute right-4 top-4 text-[var(--color-text-tertiary)] hover:text-[var(--color-text)] transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        {snap.mode === 'login' ? (
          <LoginForm onSwitchMode={() => openAuth('register')} />
        ) : (
          <RegisterForm onSwitchMode={() => openAuth('login')} />
        )}
      </div>
    </div>
  );
}
